/** Shared helpers for fuel product master data (no hardcoded MS/HSD/XP lists). */

import { fetchJSON } from '@/lib/api';

export type FuelProductRow = {
  id: string;
  product_name?: string;
  productName?: string;
  short_name?: string;
  shortName?: string;
  lfrn?: string;
  lfr?: string;
  is_active?: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
};

export function fuelProductLabel(p: FuelProductRow): string {
  const short = (p.short_name || p.shortName || "").trim();
  if (short) return short.toUpperCase();
  return (p.product_name || p.productName || "—").trim();
}

export function isActiveFuelProduct(p: FuelProductRow): boolean {
  if (p.isDeleted === true) return false;
  const active = p.is_active ?? p.isActive;
  if (active === false || String(active).toUpperCase() === "INACTIVE") return false;
  return true;
}

export function parseLfrPrice(p: FuelProductRow): number | undefined {
  const raw = String(p.lfrn ?? p.lfr ?? "").replace(/,/g, "").trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function fuelProductFilterName(p: FuelProductRow): string {
  return (p.product_name || p.productName || fuelProductLabel(p)).trim();
}

/** Match a sale/nozzle row to a master product label for grouping and pivot tables. */
export function resolveProductLabel(
  productName: string,
  fuelProductId: string | undefined,
  products: FuelProductRow[],
  byId?: Map<string, FuelProductRow>
): string {
  const map = byId ?? new Map(products.map((p) => [String(p.id), p]));
  if (fuelProductId && map.has(String(fuelProductId))) {
    return fuelProductLabel(map.get(String(fuelProductId))!);
  }
  const lower = (productName || '').toLowerCase().trim();
  if (lower) {
    for (const p of products) {
      const full = fuelProductFilterName(p).toLowerCase();
      const short = fuelProductLabel(p).toLowerCase();
      if (lower === full || lower === short || lower.includes(full) || full.includes(lower)) {
        return fuelProductLabel(p);
      }
    }
    return productName.trim();
  }
  return 'Other';
}

export async function fetchActiveFuelProducts(): Promise<FuelProductRow[]> {
  const res = await fetchJSON('/api/fuel-products');
  const data = await res.json();
  if (!res.ok || (!data.ok && !data.success)) return [];
  const rows = (data.rows || data.data || []) as FuelProductRow[];
  return rows
    .map((p) => ({ ...p, id: String(p.id ?? (p as { _id?: string })._id ?? '') }))
    .filter((p) => p.id && isActiveFuelProduct(p));
}
